import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import EventCard from "../components/EventCard";
import { MagnifyingGlass, Funnel } from "@phosphor-icons/react";

const CATS = [
  { key: "all", label: "All" },
  { key: "concerts", label: "Concerts" },
  { key: "sports", label: "Sports" },
  { key: "festivals", label: "Festivals" },
  { key: "theater", label: "Theater" },
  { key: "university", label: "University" },
  { key: "conferences", label: "Conferences" },
];

export default function EventsPage() {
  const [params, setParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const q = params.get("q") || "";
  const category = params.get("category") || "all";
  const city = params.get("city") || "all";
  const minPrice = params.get("min_price") || "";
  const maxPrice = params.get("max_price") || "";
  const sort = params.get("sort") || "date";

  useEffect(() => {
    api.get("/events/cities").then(({ data }) => setCities(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const search = {};
    if (q) search.q = q;
    if (category && category !== "all") search.category = category;
    if (city && city !== "all") search.city = city;
    if (minPrice) search.min_price = minPrice;
    if (maxPrice) search.max_price = maxPrice;
    if (sort) search.sort = sort;
    api.get("/events", { params: search })
      .then(({ data }) => setEvents(data))
      .finally(() => setLoading(false));
  }, [q, category, city, minPrice, maxPrice, sort]);

  const setParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (!v || v === "all") p.delete(k); else p.set(k, v);
    setParams(p);
  };

  return (
    <div data-testid="events-page" className="dark:bg-[#0B0F1A]">
      <div className="border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
          <span className="label-eyebrow">Discover</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 tracking-tighter dark:text-white">Every event, one place.</h1>

          <div className="mt-8 flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500" />
              <input
                value={q}
                onChange={(e) => setParam("q", e.target.value)}
                placeholder="Search events, artists, venues…"
                className="w-full border border-neutral-300 dark:border-neutral-700 dark:bg-[#151c2c] dark:text-white dark:placeholder-neutral-500 pl-12 pr-4 py-3 focus:border-black dark:focus:border-neutral-500 outline-none text-base rounded-xl"
                data-testid="search-input"
              />
            </div>
            <select
              value={city}
              onChange={(e) => setParam("city", e.target.value)}
              className="border border-neutral-300 dark:border-neutral-700 dark:bg-[#151c2c] dark:text-white px-4 py-3 outline-none focus:border-black dark:focus:border-neutral-500 bg-white rounded-xl"
              data-testid="city-filter"
            >
              <option value="all">All cities</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={sort}
              onChange={(e) => setParam("sort", e.target.value)}
              className="border border-neutral-300 dark:border-neutral-700 dark:bg-[#151c2c] dark:text-white px-4 py-3 outline-none focus:border-black dark:focus:border-neutral-500 bg-white rounded-xl"
              data-testid="sort-filter"
            >
              <option value="date">Sort: Date</option>
              <option value="price">Sort: Price</option>
              <option value="popular">Sort: Popular</option>
            </select>
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="category-pills">
            {CATS.map((c) => (
              <button
                key={c.key}
                onClick={() => setParam("category", c.key)}
                className={`px-4 py-1.5 text-xs font-bold uppercase tracking-widest border rounded-full ${category === c.key ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white" : "border-neutral-300 dark:border-neutral-700 dark:text-neutral-300 hover:border-black dark:hover:border-neutral-500"}`}
                data-testid={`pill-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
            <Funnel size={16} className="text-neutral-500 dark:text-neutral-400" />
            <span className="text-neutral-500 dark:text-neutral-400">Price (BDT):</span>
            <input value={minPrice} onChange={(e) => setParam("min_price", e.target.value)} placeholder="Min"
              className="w-24 border border-neutral-300 dark:border-neutral-700 dark:bg-[#151c2c] dark:text-white dark:placeholder-neutral-500 px-3 py-1.5 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" data-testid="min-price" />
            <span className="dark:text-neutral-300">—</span>
            <input value={maxPrice} onChange={(e) => setParam("max_price", e.target.value)} placeholder="Max"
              className="w-24 border border-neutral-300 dark:border-neutral-700 dark:bg-[#151c2c] dark:text-white dark:placeholder-neutral-500 px-3 py-1.5 outline-none focus:border-black dark:focus:border-neutral-500 rounded-lg" data-testid="max-price" />
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        {loading ? (
          <div className="text-center py-20 text-neutral-500 dark:text-neutral-400" data-testid="events-loading">Loading events…</div>
        ) : events.length === 0 ? (
          <div className="text-center py-20" data-testid="events-empty">
            <p className="font-display text-3xl dark:text-white">No events match your search.</p>
            <p className="text-neutral-500 dark:text-neutral-400 mt-2">Try adjusting your filters.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4" data-testid="events-count">{events.length} events</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="events-grid">
              {events.map((e) => <EventCard key={e.id} event={e} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}